import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Warning } from '@phosphor-icons/react';

interface DashboardWidgetProps {
  value: number | string;
  label: string;
  linkText?: string;
  linkTo?: string;
  variant?: 'default' | 'highlight' | 'blue';
  isLoading?: boolean;
  isError?: boolean;
}

const variantStyles = {
  default: {
    bg: 'bg-[#f4f5f6]',
    text: 'text-gray-900',
    link: 'text-[#5c6670]',
  },
  highlight: {
    bg: 'bg-[#d4edda]',
    text: 'text-[#155724]',
    link: 'text-[#155724]',
  },
  blue: {
    bg: 'bg-blue-100',
    text: 'text-blue-900',
    link: 'text-blue-700',
  },
};

export const DashboardWidget = ({
  value,
  label,
  linkText,
  linkTo,
  variant = 'default',
  isLoading = false,
  isError = false,
}: DashboardWidgetProps) => {
  const { t } = useTranslation();
  const styles = variantStyles[variant];
  const hasLink = linkText !== undefined && linkTo !== undefined;

  if (isLoading) {
    return (
      <div className="flex flex-col rounded-md overflow-hidden shadow-[0px_4px_24px_0px_rgba(0,41,102,0.04)]">
        <div className="flex items-end justify-center py-3 bg-white/55">
          <div className="h-12 w-16 bg-gray-200 rounded animate-pulse" />
        </div>
        <div className="flex flex-col items-center gap-1 px-3 pt-2 pb-1 bg-[#f4f5f6]">
          <div className="h-5 w-32 bg-gray-200 rounded animate-pulse" />
          <div className="h-8 w-24 bg-gray-200 rounded animate-pulse my-1" />
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col rounded-md overflow-hidden shadow-[0px_4px_24px_0px_rgba(0,41,102,0.04)]">
        <div className="flex items-center justify-center py-3 bg-white/55">
          <Warning size={48} weight="regular" className="text-red-400" />
        </div>
        <div className="flex flex-col items-center gap-1 px-3 pt-2 pb-3 bg-red-50">
          <span className="text-sm font-medium text-center text-red-600">
            {t('dashboard.widget.error')}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col rounded-md overflow-hidden shadow-[0px_4px_24px_0px_rgba(0,41,102,0.04)]">
      <div className="flex items-end justify-center py-3 bg-white/55">
        <span
          className="text-5xl font-display font-light text-[#5c88a3]"
          style={{ fontFeatureSettings: "'lnum' 1, 'pnum' 1" }}
        >
          {value}
        </span>
      </div>
      <div
        className={`flex flex-col items-center gap-1 px-3 pt-2 ${hasLink ? 'pb-1' : 'pb-3'} ${styles.bg}`}
      >
        <span className={`text-sm font-medium text-center ${styles.text}`}>{label}</span>
        {hasLink && (
          <Link
            to={linkTo}
            className={`flex items-center gap-2 py-2 px-0.5 text-sm font-medium hover:underline ${styles.link}`}
          >
            {linkText}
            <ArrowRight size={24} weight="regular" />
          </Link>
        )}
      </div>
    </div>
  );
};
